import { z } from "zod";

import type { DeviceAllowedCodeLength } from "@jti/fe-common";
import { DEVICE_CODE_POSSIBLE_LENGTH } from "@jti/fe-common";

import { isReferenceObjectWithValue } from "../tools/isReferenceObjectWithValue";

const AGE_VERIFICATION_METHOD_IMAGES_SCHEMA = z.object({
  data: z.object({
    imagesList: z.array(
      z.object({
        methodKey: z.string(),
        methodImage: z.string(),
      })
    ),
  }),
});

type AgeVerificationMethodImages = z.infer<typeof AGE_VERIFICATION_METHOD_IMAGES_SCHEMA>;

export const DEVICE_CODE_LENGTH_MAP = {
  short: DEVICE_CODE_POSSIBLE_LENGTH.SHORT,
  long: DEVICE_CODE_POSSIBLE_LENGTH.LONG,
  gen4: DEVICE_CODE_POSSIBLE_LENGTH.GEN4,
} as const;

type DeviceCodeLengthConfig = {
  short?: boolean;
  long?: boolean;
  gen4?: boolean;
};

export const mapDeviceCodeLengths = (deviceCodeLength?: DeviceCodeLengthConfig): DeviceAllowedCodeLength[] => {
  if (!deviceCodeLength) {
    return [DEVICE_CODE_LENGTH_MAP.short];
  }

  return Object.entries(deviceCodeLength).reduce<DeviceAllowedCodeLength[]>((acc, [key, isEnabled]) => {
    if (isEnabled && key in DEVICE_CODE_LENGTH_MAP) {
      acc.push(DEVICE_CODE_LENGTH_MAP[key as keyof typeof DEVICE_CODE_LENGTH_MAP]);
    }
    return acc;
  }, []);
};

export const mapAgeVerificationMethodImages = (ageVerificationDataModel: unknown) => {
  if (
    isReferenceObjectWithValue<AgeVerificationMethodImages>(ageVerificationDataModel) &&
    AGE_VERIFICATION_METHOD_IMAGES_SCHEMA.safeParse(ageVerificationDataModel.value).success
  ) {
    return ageVerificationDataModel.value.data.imagesList.reduce<Record<string, string>>((acc, curr) => {
      acc[curr.methodKey] = curr.methodImage;
      return acc;
    }, {});
  }

  return {};
};

const LOYALTY_SCHEMA = z.object({
  data: z.object({
    loyalty: z
      .object({
        showMgm: z.boolean().optional(),
        showReminderDialog: z.boolean().optional(),
        reminderDialogSKUs: z.string().optional(),
        reminderDialogTitle: z.string().optional(),
        reminderDialogSubtitle: z.string().optional(),
        reminderDialogText: z.string().optional(),
        reminderDialogSecondaryButtonText: z.string().optional(),
        reminderDialogSecondaryButtonLink: z.string().optional(),
      })
      .optional(),
  }),
});

type CheckoutLoyaltyReference = z.infer<typeof LOYALTY_SCHEMA>;

export const mapLoyalty = (checkoutConfigurationDataModel: unknown) => {
  if (
    isReferenceObjectWithValue<CheckoutLoyaltyReference>(checkoutConfigurationDataModel) &&
    LOYALTY_SCHEMA.safeParse(checkoutConfigurationDataModel.value).success
  ) {
    const loyalty = checkoutConfigurationDataModel.value.data.loyalty;

    return {
      showMgm: loyalty?.showMgm ?? false,
      showReminderDialog: loyalty?.showReminderDialog ?? false,
      reminderDialogSKUs: loyalty?.reminderDialogSKUs ?? "",
      reminderDialogTitle: loyalty?.reminderDialogTitle ?? "",
      reminderDialogSubtitle: loyalty?.reminderDialogSubtitle ?? "",
      reminderDialogText: loyalty?.reminderDialogText ?? "",
      reminderDialogSecondaryButtonText: loyalty?.reminderDialogSecondaryButtonText ?? "",
      reminderDialogSecondaryButtonLink: loyalty?.reminderDialogSecondaryButtonLink ?? "",
    };
  }

  return {
    showMgm: false,
    showReminderDialog: false,
    reminderDialogSKUs: "",
    reminderDialogTitle: "",
    reminderDialogSubtitle: "",
    reminderDialogText: "",
    reminderDialogSecondaryButtonText: "",
    reminderDialogSecondaryButtonLink: "",
  };
};
